import React, { useEffect, useState, useCallback } from "react";
import {
  LogIn, LogOut, Clock, Users, Calendar as CalIcon, Loader2, ShieldCheck,
  UserCog, ChevronLeft, ChevronRight, Activity, TrendingUp,
  Coffee, Play, MapPin, AlertTriangle, Download, Settings as SettingsIcon, Bell
} from "lucide-react";
import { attendanceApi, teamApi } from "../api";
import { useAuth } from "../AuthContext";
const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};
const fmtMin = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${r}m`;
};
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shiftDate = (k, delta) => {
  const d = new Date(k + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

function StatusPill({ status }) {
  const map = {
    in: { label: "Clocked in", color: "#34d399", bg: "rgba(52,211,153,0.18)" },
    out: { label: "Clocked out", color: "#a78bfa", bg: "rgba(103,232,249,0.16)" },
    absent: { label: "Absent", color: "#c7c1ff", bg: "rgba(168,227,239,0.10)" },
    on_leave: { label: "On leave", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  };
  const m = map[status] || map.absent;
  return (
    <span
      className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 inline-flex items-center gap-1"
      style={{ color: m.color, background: m.bg, border: `1px solid ${m.color}55` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

function EmployeeView() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([attendanceApi.meToday(), attendanceApi.meHistory(14)]);
      setToday(t);
      setHistory(h);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const clockedIn = !!(today?.clock_in && !today?.clock_out);
  const onBreak = !!(today?.breaks?.length && !today.breaks[today.breaks.length - 1]?.end);

  let liveMinutes = null;
  if (clockedIn) {
    liveMinutes = Math.floor((Date.now() - new Date(today.clock_in).getTime()) / 60000);
  }

  const getGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, label: "" }),
      () => resolve(null),
      { timeout: 4000, enableHighAccuracy: false }
    );
  });

  const doClockIn = async () => {
    setBusy(true);
    try {
      const geo = await getGeo();
      await attendanceApi.clockIn({ geo });
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };
  const doClockOut = async () => {
    setBusy(true);
    try {
      const geo = await getGeo();
      await attendanceApi.clockOut({ geo });
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };
  const doBreakToggle = async () => {
    setBusy(true);
    try {
      if (onBreak) await attendanceApi.breakEnd();
      else await attendanceApi.breakStart();
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="p-6 max-w-[920px] mx-auto space-y-6">
      <div
        className="panel p-6"
        style={{
          clipPath: "polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))",
          background: clockedIn
            ? "linear-gradient(160deg, rgba(52,211,153,0.18), rgba(52,211,153,0.04))"
            : "linear-gradient(160deg, rgba(139,92,246,0.14), rgba(139,92,246,0.04))",
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.35em] text-[#c7c1ff]/65">
              // {onBreak ? "on break" : (clockedIn ? "session active" : "today's session")}
            </div>
            <div className="text-2xl font-light tracking-tight mt-1">
              {user?.name || user?.username}
            </div>
            <div className="mono text-[10px] text-[#c7c1ff]/55 mt-1 uppercase tracking-widest flex items-center gap-2">
              {fmtDate(new Date().toISOString())}
              {today?.is_late && (
                <span className="px-1.5 py-0.5 text-[#f87171]" style={{ background: "rgba(248,113,113,0.16)", border: "1px solid rgba(248,113,113,0.4)" }}>
                  late {today.late_minutes}m
                </span>
              )}
              {today?.is_overtime && (
                <span className="px-1.5 py-0.5 text-[#3b82f6]" style={{ background: "rgba(59,130,246,0.16)", border: "1px solid rgba(59,130,246,0.4)" }}>
                  overtime +{today.overtime_minutes}m
                </span>
              )}
              {today?.location?.label && (
                <span className="flex items-center gap-1 text-[#8b5cf6]"><MapPin size={9} /> {today.location.label}</span>
              )}
            </div>
            <div className="mt-4 flex items-center gap-5 text-sm flex-wrap">
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">Clock In</div>
                <div className="text-lg mono mt-0.5" style={{ color: today?.clock_in ? "#34d399" : "#c7c1ff" }}>
                  {fmtTime(today?.clock_in)}
                </div>
              </div>
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">Clock Out</div>
                <div className="text-lg mono mt-0.5" style={{ color: today?.clock_out ? "#a78bfa" : "#c7c1ff" }}>
                  {fmtTime(today?.clock_out)}
                </div>
              </div>
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">Work Time</div>
                <div className="text-lg mono mt-0.5 text-[#3b82f6]">
                  {today?.clock_out ? fmtMin(today.work_minutes ?? today.duration_min) : (clockedIn ? `${fmtMin(Math.max(0, (liveMinutes || 0) - (today?.break_minutes || 0)))} (live)` : "—")}
                </div>
              </div>
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">Breaks</div>
                <div className="text-lg mono mt-0.5 text-[#a78bfa]">
                  {fmtMin(today?.break_minutes || 0)} <span className="text-[10px] text-[#c7c1ff]/40">({today?.breaks?.length || 0})</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            <StatusPill status={onBreak ? "out" : (clockedIn ? "in" : (today?.clock_out ? "out" : "absent"))} />
            {!clockedIn ? (
              <button
                data-testid="clock-in-btn"
                onClick={doClockIn}
                disabled={busy || (today?.clock_in && today?.clock_out)}
                className="px-6 py-3 mono text-xs uppercase tracking-[0.3em] font-bold flex items-center gap-2 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#34d399,#8b5cf6)", color: "#062c25", clipPath: "polygon(8px 0,100% 0,calc(100% - 8px) 100%,0 100%)" }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
                Clock In
              </button>
            ) : (
              <>
                <button
                  data-testid="break-btn"
                  onClick={doBreakToggle}
                  disabled={busy}
                  className="px-4 py-2 mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-1.5"
                  style={{
                    background: onBreak ? "rgba(167,139,250,0.22)" : "rgba(167,139,250,0.1)",
                    border: "1px solid rgba(167,139,250,0.5)",
                    color: "#c4b5fd",
                  }}
                >
                  {onBreak ? <><Play size={11} /> end break</> : <><Coffee size={11} /> start break</>}
                </button>
                <button
                  data-testid="clock-out-btn"
                  onClick={doClockOut}
                  disabled={busy || onBreak}
                  title={onBreak ? "End break first" : "Clock out"}
                  className="px-6 py-3 mono text-xs uppercase tracking-[0.3em] font-bold flex items-center gap-2 disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg,#3b82f6,#f87171)", color: "#1a0a0a", clipPath: "polygon(8px 0,100% 0,calc(100% - 8px) 100%,0 100%)" }}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                  Clock Out
                </button>
              </>
            )}
            {today?.clock_in && today?.clock_out && (
              <button
                data-testid="resume-btn"
                onClick={doClockIn}
                disabled={busy}
                className="btn-ghost text-[10px] uppercase tracking-widest flex items-center gap-1 mono"
              >
                <LogIn size={11} /> Resume Session
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <CalIcon size={13} color="#8b5cf6" />
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#c7c1ff]">// 14-day history</div>
        </div>
        <div className="panel p-3 overflow-x-auto">
          {history.length === 0 ? (
            <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest py-2 text-center">// no records yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#8b5cf6]/15 mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">In</th>
                  <th className="text-left py-2 px-2">Out</th>
                  <th className="text-left py-2 px-2">Work</th>
                  <th className="text-left py-2 px-2">Break</th>
                  <th className="text-left py-2 px-2">Flags</th>
                  <th className="text-left py-2 px-2">Location</th>
                </tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.id} className="border-b border-[#8b5cf6]/8" data-testid={`history-row-${r.date}`}>
                    <td className="py-2 px-2 mono text-xs">{r.date}</td>
                    <td className="py-2 px-2 mono text-xs text-[#34d399]">{fmtTime(r.clock_in)}</td>
                    <td className="py-2 px-2 mono text-xs text-[#a78bfa]">{fmtTime(r.clock_out)}</td>
                    <td className="py-2 px-2 mono text-xs text-[#3b82f6]">{fmtMin(r.work_minutes ?? r.duration_min)}</td>
                    <td className="py-2 px-2 mono text-xs text-[#a78bfa]">{fmtMin(r.break_minutes || 0)}</td>
                    <td className="py-2 px-2 mono text-[10px] uppercase tracking-widest space-x-1">
                      {r.is_late && <span className="text-[#f87171]">late</span>}
                      {r.is_overtime && <span className="text-[#3b82f6]">ot</span>}
                    </td>
                    <td className="py-2 px-2 text-xs text-[#c7c1ff]/70 truncate max-w-[180px]">
                      {r.location?.label || (r.location?.lat ? `${r.location.lat.toFixed(3)},${r.location.lng.toFixed(3)}` : (r.location?.ip || "—"))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function BossView() {
  const { user: meUser } = useAuth();
  const companyLabel = meUser?.company_name || (meUser?.address?.split("@")[0] + "'s workspace");
  const [date, setDate] = useState(todayKey());
  const [team, setTeam] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRoster, setShowRoster] = useState(false);
  const [roster, setRoster] = useState([]);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [overstayBusy, setOverstayBusy] = useState(false);
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [empForm, setEmpForm] = useState({ username: "", name: "", password: "" });
  const [empBusy, setEmpBusy] = useState(false);
  const [empErr, setEmpErr] = useState("");

  const load = useCallback(async (d = date) => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([attendanceApi.team(d), attendanceApi.teamSummary(7)]);
      setTeam(t);
      setSummary(s);
      if (t?.policy) setPolicy(t.policy);
    } catch { /* ignore */ }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(date); }, [date, load]);
  useEffect(() => {
    const t = setInterval(() => load(date), 20000);
    return () => clearInterval(t);
  }, [date, load]);

  const loadRoster = async () => { try { setRoster(await teamApi.list()); } catch { /* ignore */ } };
  const loadAlerts = async () => { try { setAlerts(await attendanceApi.alerts()); } catch { /* ignore */ } };

  const toggleRoster = async () => { if (!showRoster) await loadRoster(); setShowRoster(s => !s); };
  const toggleAlerts = async () => { if (!showAlerts) await loadAlerts(); setShowAlerts(s => !s); };
  const togglePolicy = async () => { if (!showPolicy && !policy) { try { setPolicy(await attendanceApi.getPolicy()); } catch { /* ignore */ } } setShowPolicy(s => !s); };

  const promote = async (addr, role) => { await teamApi.setRole(addr, role); loadRoster(); load(date); };
  const submitAddEmp = async () => {
    setEmpErr("");
    if (!empForm.username.trim()) { setEmpErr("Username required."); return; }
    if (!empForm.password || empForm.password.length < 6) { setEmpErr("Temporary password (6+ chars) required."); return; }
    setEmpBusy(true);
    try {
      await teamApi.createEmployee(empForm.username.trim(), empForm.password, empForm.name.trim());
      setShowAddEmp(false);
      setEmpForm({ username: "", name: "", password: "" });
      loadRoster();
      load(date);
    } catch (e) {
      setEmpErr(e?.response?.data?.detail || "Could not create employee.");
    }
    setEmpBusy(false);
  };
  const savePolicy = async () => { const p = await attendanceApi.setPolicy(policy); setPolicy(p); load(date); };
  const runOverstayCheck = async () => {
    setOverstayBusy(true);
    try { const r = await attendanceApi.checkOverstays(); alert(`Checked ${r.checked} active sessions, ${r.overstays} overstay(s) detected, ${r.notified_bosses} boss(es) notified.`); await loadAlerts(); } catch { /* ignore */ }
    setOverstayBusy(false);
  };
  const downloadCsv = async () => {
    setExportBusy(true);
    try {
      const { url, token } = attendanceApi.exportUrl(exportRange.from || undefined, exportRange.to || undefined);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `hexa_attendance_${exportRange.from || "30d"}_to_${exportRange.to || todayKey()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { /* ignore */ }
    setExportBusy(false);
  };

  const totals = team?.totals || { present: 0, active: 0, absent: 0, late: 0, overtime: 0, overstay: 0, employees: 0 };
  const maxMin = Math.max(1, ...(summary?.rows || []).map(r => r.total_minutes));

  return (
    <div className="p-5 space-y-5 overflow-y-auto h-full">
      <div className="flex items-center gap-3 flex-wrap">
        <ShieldCheck size={14} color="#3b82f6" />
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// boss view · team attendance</div>
        <span
          data-testid="boss-workspace-label"
          className="mono text-[9px] uppercase tracking-[0.25em] text-[#a78bfa] border border-[#8b5cf6]/35 px-2 py-0.5"
          title="Your private workspace — employees outside this workspace are not visible to you"
        >· {companyLabel}</span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button onClick={() => setDate(shiftDate(date, -1))} className="p-1.5 hover:bg-[#8b5cf6]/10" data-testid="boss-date-prev"><ChevronLeft size={14} /></button>
          <input
            data-testid="boss-date-input"
            type="date"
            value={date}
            max={todayKey()}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1 mono text-xs outline-none focus:border-[#8b5cf6]/60"
          />
          <button onClick={() => setDate(shiftDate(date, 1))} disabled={date >= todayKey()} className="p-1.5 hover:bg-[#8b5cf6]/10 disabled:opacity-30" data-testid="boss-date-next"><ChevronRight size={14} /></button>
          <button onClick={() => setDate(todayKey())} className="btn-ghost text-[10px] uppercase tracking-widest mono">today</button>
          <button onClick={toggleAlerts} className={`btn-ghost text-[10px] uppercase tracking-widest mono flex items-center gap-1 ${showAlerts ? "text-[#3b82f6]" : ""}`} data-testid="toggle-alerts"><Bell size={11} /> alerts</button>
          <button onClick={togglePolicy} className={`btn-ghost text-[10px] uppercase tracking-widest mono flex items-center gap-1 ${showPolicy ? "text-[#3b82f6]" : ""}`} data-testid="toggle-policy"><SettingsIcon size={11} /> policy</button>
          <button onClick={toggleRoster} className={`btn-ghost text-[10px] uppercase tracking-widest mono flex items-center gap-1 ${showRoster ? "text-[#3b82f6]" : ""}`} data-testid="toggle-roster"><UserCog size={11} /> roster</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { id: "active", label: "Active Now", value: totals.active, color: "#34d399", icon: Activity },
          { id: "present", label: "Present", value: totals.present, color: "#8b5cf6", icon: LogIn },
          { id: "absent", label: "Absent", value: totals.absent, color: "#c7c1ff", icon: LogOut },
          { id: "late", label: "Late", value: totals.late, color: "#f87171", icon: AlertTriangle },
          { id: "overtime", label: "Overtime", value: totals.overtime, color: "#3b82f6", icon: TrendingUp },
          { id: "overstay", label: "Overstay", value: totals.overstay, color: "#fb923c", icon: AlertTriangle },
        ].map(k => {
          const Icon = k.icon;
          return (
            <div
              key={k.id}
              data-testid={`boss-kpi-${k.id}`}
              className="p-3"
              style={{
                background: `linear-gradient(160deg, ${k.color}22, ${k.color}08)`,
                border: `1px solid ${k.color}44`,
                clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
              }}
            >
              <div className="flex items-center gap-1.5 mono text-[9px] uppercase tracking-widest" style={{ color: k.color }}>
                <Icon size={10} /> {k.label}
              </div>
              <div className="text-2xl font-light mono mt-1" style={{ color: k.color }}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="date"
          value={exportRange.from}
          onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })}
          placeholder="from"
          className="bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1 mono text-[11px] outline-none"
          data-testid="export-from"
        />
        <span className="mono text-[10px] text-[#c7c1ff]/40">→</span>
        <input
          type="date"
          value={exportRange.to}
          onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })}
          placeholder="to"
          className="bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1 mono text-[11px] outline-none"
          data-testid="export-to"
        />
        <button
          data-testid="export-csv"
          onClick={downloadCsv}
          disabled={exportBusy}
          className="btn-accent text-[10px] uppercase tracking-widest flex items-center gap-1 mono"
        >
          {exportBusy ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} export csv
        </button>
        <button
          data-testid="check-overstays"
          onClick={runOverstayCheck}
          disabled={overstayBusy}
          className="btn-ghost text-[10px] uppercase tracking-widest flex items-center gap-1 mono ml-auto"
        >
          {overstayBusy ? <Loader2 size={11} className="animate-spin" /> : <AlertTriangle size={11} />} run overstay check
        </button>
      </div>

      {/* Policy editor */}
      {showPolicy && policy && (
        <div className="panel p-4" data-testid="policy-panel">
          <div className="mono text-[10px] uppercase tracking-widest text-[#3b82f6] mb-3">// work policy</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">work start</div>
              <input data-testid="policy-start" value={policy.work_start_time} onChange={(e) => setPolicy({ ...policy, work_start_time: e.target.value })} placeholder="09:30" className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 mono outline-none focus:border-[#8b5cf6]/60" />
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">work end</div>
              <input data-testid="policy-end" value={policy.work_end_time} onChange={(e) => setPolicy({ ...policy, work_end_time: e.target.value })} placeholder="18:00" className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 mono outline-none focus:border-[#8b5cf6]/60" />
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">regular hrs/day</div>
              <input data-testid="policy-hrs" type="number" step="0.5" value={policy.regular_hours_per_day} onChange={(e) => setPolicy({ ...policy, regular_hours_per_day: Number(e.target.value) })} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 mono outline-none focus:border-[#8b5cf6]/60" />
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">late grace (min)</div>
              <input data-testid="policy-grace" type="number" value={policy.late_grace_minutes} onChange={(e) => setPolicy({ ...policy, late_grace_minutes: Number(e.target.value) })} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 mono outline-none focus:border-[#8b5cf6]/60" />
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">overstay max (min)</div>
              <input data-testid="policy-overstay" type="number" value={policy.overstay_max_minutes} onChange={(e) => setPolicy({ ...policy, overstay_max_minutes: Number(e.target.value) })} className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-2 py-1.5 mono outline-none focus:border-[#8b5cf6]/60" />
            </div>
          </div>
          <button data-testid="policy-save" onClick={savePolicy} className="btn-accent text-[10px] uppercase tracking-widest mono mt-3 flex items-center gap-1"><SettingsIcon size={10} /> save policy</button>
        </div>
      )}

      {/* Alerts panel */}
      {showAlerts && (
        <div className="panel p-4" data-testid="alerts-panel">
          <div className="mono text-[10px] uppercase tracking-widest text-[#fb923c] mb-3 flex items-center gap-1"><Bell size={11} /> // overstay alerts ({alerts.length})</div>
          {alerts.length === 0 ? (
            <div className="mono text-[10px] text-[#c7c1ff]/45 uppercase tracking-widest py-2">// no alerts yet</div>
          ) : (
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {alerts.map(a => (
                <div key={a.id} className="bg-[#fb923c]/8 border border-[#fb923c]/25 p-2 text-xs flex items-start gap-2" data-testid={`alert-${a.id}`}>
                  <AlertTriangle size={11} className="text-[#fb923c] mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[#fb923c]">{a.body}</div>
                    <div className="mono text-[9px] text-[#c7c1ff]/45 mt-0.5">{new Date(a.ts).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showRoster && (
        <div className="panel p-3" data-testid="roster-panel">
          <div className="flex items-center justify-between px-2 py-1">
            <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60">// roster · click role to toggle</div>
            <button
              data-testid="add-employee-open"
              onClick={() => { setShowAddEmp(true); setEmpErr(""); }}
              className="mono text-[10px] uppercase tracking-widest text-[#3b82f6] hover:text-[#60a5fa] flex items-center gap-1 px-2 py-1 border border-[#3b82f6]/40 hover:border-[#3b82f6]"
            >+ add employee</button>
          </div>
          {roster.map(u => (
            <div key={u.address} className="flex items-center gap-3 py-2 px-2 border-b border-[#8b5cf6]/8 last:border-0">
              <div className="flex-1">
                <div className="text-sm">{u.name}</div>
                <div className="mono text-[10px] text-[#c7c1ff]/50">{u.address}</div>
              </div>
              <button
                data-testid={`roster-role-${u.address}`}
                onClick={() => promote(u.address, u.role === "boss" ? "employee" : "boss")}
                className="px-3 py-1 mono text-[10px] uppercase tracking-widest"
                style={{
                  background: u.role === "boss" ? "rgba(59,130,246,0.18)" : "rgba(139,92,246,0.12)",
                  border: `1px solid ${u.role === "boss" ? "#3b82f6" : "#8b5cf6"}55`,
                  color: u.role === "boss" ? "#3b82f6" : "#a78bfa",
                }}
              >{u.role}</button>
            </div>
          ))}
        </div>
      )}

      <div className="panel p-3 overflow-x-auto">
        <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/60 px-2 py-1">
          // attendance · {date} {date === todayKey() && <span className="text-[#34d399]">· live</span>}
        </div>
        {loading && !team ? (
          <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest py-4 text-center">loading…</div>
        ) : (team?.rows || []).length === 0 ? (
          <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest py-4 text-center">// no team members</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#8b5cf6]/15 mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55">
                <th className="text-left py-2 px-2">Employee</th>
                <th className="text-left py-2 px-2">Role</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">In</th>
                <th className="text-left py-2 px-2">Out</th>
                <th className="text-left py-2 px-2">Work</th>
                <th className="text-left py-2 px-2">Break</th>
                <th className="text-left py-2 px-2">Flags</th>
                <th className="text-left py-2 px-2">Location</th>
              </tr>
            </thead>
            <tbody>
              {(team?.rows || []).map(r => (
                <tr
                  key={r.address}
                  className="border-b border-[#8b5cf6]/8"
                  style={r.is_overstay ? { background: "rgba(251,146,60,0.08)" } : (r.is_late ? { background: "rgba(248,113,113,0.05)" } : {})}
                  data-testid={`team-row-${r.address}`}
                >
                  <td className="py-2 px-2">
                    <div className="text-sm">{r.name}</div>
                    <div className="mono text-[10px] text-[#c7c1ff]/50">{r.address}</div>
                  </td>
                  <td className="py-2 px-2 mono text-[10px] uppercase tracking-widest" style={{ color: r.role === "boss" ? "#3b82f6" : "#c7c1ff" }}>{r.role}</td>
                  <td className="py-2 px-2"><StatusPill status={r.status} /></td>
                  <td className="py-2 px-2 mono text-xs text-[#34d399]">{fmtTime(r.clock_in)}</td>
                  <td className="py-2 px-2 mono text-xs text-[#a78bfa]">{fmtTime(r.clock_out)}</td>
                  <td className="py-2 px-2 mono text-xs text-[#3b82f6]">{fmtMin(r.work_minutes ?? r.duration_min)}</td>
                  <td className="py-2 px-2 mono text-xs text-[#a78bfa]">{fmtMin(r.break_minutes || 0)}</td>
                  <td className="py-2 px-2 mono text-[10px] uppercase tracking-widest space-x-1 whitespace-nowrap">
                    {r.is_late && <span className="px-1 py-0.5 text-[#f87171]" style={{ background: "rgba(248,113,113,0.16)", border: "1px solid rgba(248,113,113,0.4)" }}>late {r.late_minutes}m</span>}
                    {r.is_overtime && <span className="px-1 py-0.5 text-[#3b82f6]" style={{ background: "rgba(59,130,246,0.16)", border: "1px solid rgba(59,130,246,0.4)" }}>ot +{r.overtime_minutes}m</span>}
                    {r.is_overstay && <span className="px-1 py-0.5 text-[#fb923c]" style={{ background: "rgba(251,146,60,0.16)", border: "1px solid rgba(251,146,60,0.4)" }}>overstay</span>}
                  </td>
                  <td className="py-2 px-2 text-xs text-[#c7c1ff]/70 truncate max-w-[160px]">
                    {r.location?.label || (r.location?.lat ? `${r.location.lat.toFixed(3)},${r.location.lng.toFixed(3)}` : (r.location?.ip || "—"))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={13} color="#8b5cf6" />
          <div className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/70">// hours worked · last 7 days</div>
        </div>
        {(summary?.rows || []).length === 0 ? (
          <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest py-3 text-center">// no data</div>
        ) : (
          <div className="space-y-2">
            {(summary?.rows || []).map(r => {
              const pct = Math.max(2, Math.round((r.total_minutes / maxMin) * 100));
              return (
                <div key={r.address} className="flex items-center gap-3" data-testid={`summary-row-${r.address}`}>
                  <div className="w-36 text-xs truncate">{r.name}</div>
                  <div className="flex-1 h-4 bg-[#0e1a22] border border-[#8b5cf6]/15 relative">
                    <div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#8b5cf6,#3b82f6)" }} />
                  </div>
                  <div className="mono text-xs text-[#3b82f6] w-24 text-right">{fmtMin(r.total_minutes)}</div>
                  <div className="mono text-[10px] text-[#c7c1ff]/55 w-16 text-right">{r.days_present}d</div>
                  {r.late_days > 0 && <div className="mono text-[10px] text-[#f87171] w-16 text-right">{r.late_days} late</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddEmp && (
        <div
          data-testid="add-employee-modal"
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[300]"
          onClick={() => setShowAddEmp(false)}
        >
          <div
            className="w-[420px] p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              border: "1px solid rgba(59,130,246,0.30)",
              clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="mono text-[11px] uppercase tracking-widest text-[#3b82f6]">// add employee</div>
              <button onClick={() => setShowAddEmp(false)} className="text-white/60 hover:text-white">×</button>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[#c7c1ff]/50 mb-1 mono">username</div>
              <div className="flex items-center bg-[#0e1a22] border border-[#8b5cf6]/20 focus-within:border-[#3b82f6]/60">
                <input
                  data-testid="add-emp-username"
                  value={empForm.username}
                  onChange={(e) => setEmpForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="employee.name"
                  className="flex-1 bg-transparent px-3 py-2 text-sm outline-none mono"
                  autoFocus
                />
                <span className="text-xs text-[#3b82f6]/80 px-3 mono">@hexawebos.com</span>
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[#c7c1ff]/50 mb-1 mono">display name</div>
              <input
                data-testid="add-emp-name"
                value={empForm.name}
                onChange={(e) => setEmpForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Their name"
                className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#3b82f6]/60 mono"
              />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[#c7c1ff]/50 mb-1 mono">temporary password</div>
              <input
                data-testid="add-emp-password"
                value={empForm.password}
                onChange={(e) => setEmpForm(f => ({ ...f, password: e.target.value }))}
                placeholder="at least 6 characters"
                className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#3b82f6]/60 mono"
              />
              <div className="text-[9px] mono text-[#c7c1ff]/45 mt-1">// share this with them · they'll change it after first login</div>
            </div>
            {empErr && <div className="text-xs text-[#f87171] mono">{empErr}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowAddEmp(false)} className="btn-ghost text-xs">Cancel</button>
              <button
                data-testid="add-emp-submit"
                onClick={submitAddEmp}
                disabled={empBusy}
                className="btn-accent text-xs flex items-center gap-1"
              >
                {empBusy && <Loader2 size={11} className="animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const isBoss = user?.role === "boss";
  const isIndividual = user?.role === "individual";
  const [tab, setTab] = useState(isBoss ? "boss" : "me");

  useEffect(() => {
    setTab(isBoss ? "boss" : "me");
  }, [isBoss]);

  if (isIndividual) {
    return (
      <div
        data-testid="attendance-individual-locked"
        className="h-full flex flex-col items-center justify-center text-center px-6"
        style={{ background: "#ffffff" }}
      >
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#3b82f6] mb-2">// individual account</div>
        <div className="text-base text-white mb-1 max-w-md">Attendance is a Business-only feature</div>
        <div className="text-xs text-[#c7c1ff]/55 max-w-md leading-relaxed">
          Your HEXA ID was created as an Individual account, so there's no team to track here.
          Upgrade to a Business workspace from your profile to add employees and clock-in/out.
        </div>
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col" style={{ background: "#ffffff" }}>
      <div className="border-b border-[#8b5cf6]/15 px-3 flex items-center">
        <button
          onClick={() => setTab("me")}
          data-testid="att-tab-me"
          className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-2 ${tab === "me" ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
          style={{ borderBottom: tab === "me" ? "2px solid #3b82f6" : "2px solid transparent" }}
        >
          <Clock size={12} /> My Attendance
        </button>
        {isBoss && (
          <button
            onClick={() => setTab("boss")}
            data-testid="att-tab-boss"
            className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-2 ${tab === "boss" ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
            style={{ borderBottom: tab === "boss" ? "2px solid #3b82f6" : "2px solid transparent" }}
          >
            <Users size={12} /> Team View
            <span className="ml-1 mono text-[8px] px-1 py-0.5 bg-[#3b82f6] text-[#0b1014] uppercase">boss</span>
          </button>
        )}
        <div className="ml-auto mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/45 pr-2">
          role: <span style={{ color: isBoss ? "#3b82f6" : "#a78bfa" }}>{user?.role || "employee"}</span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {tab === "me" ? <EmployeeView /> : <BossView />}
      </div>
    </div>
  );
}
